import { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { usePrefixedNavigate } from "../../hooks/usePrefixedNavigate";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Clock,
  CheckCircle,
  XCircle,
  Mail,
  Phone,
  Globe,
  Shield,
  Upload,
  Play,
  Eye,
  X,
} from "lucide-react";
import { Dialog, DialogContent } from "../../components/ui/dialog";
import { toast } from "sonner";
import {
  facilitiesMasterAPI,
  institutionsAPI,
  statesAPI,
  designationMasterAPI,
  type Institution,
  type InstitutionApproveResponse,
  type InstitutionVoteInfo,
  type DesignationMaster,
} from "../../services/api";
import { formatDate } from "../../lib/utils";
import {
  INSTITUTION_ONBOARDING_STEPS,
  INSTITUTION_TYPES,
  BOARDS,
  MEDIUM_OF_INSTRUCTION,
  INSTITUTION_LEVELS,
  BOARDING_GENDER,
} from "../../constants/institutionFormFields";
import { useAuth } from "../../contexts/AuthContext";
import { getUserInfo } from "@/lib/storeManager";
import { toInstitutionTitleCase } from "@/projects/Jainboarding_User/utils/institutionFormat";

type InstitutionDetailData = Institution & {
  id: string | number;
  approval_status?: string | null;
  approval_notes?: string | null;
  state_main?: string | null;
  district_main?: string | null;
  city_main?: string | null;
  area_main?: string | null;
  state_main_name?: string | null;
  district_main_name?: string | null;
  city_main_name?: string | null;
  state_name?: string | null;
  district_name?: string | null;
  city_name?: string | null;
  teaching_staff?: number | null;
  non_teaching_staff?: number | null;
  total_students_in_school?: number | null;
  total_classroom_in_school?: number | null;
  total_students_in_hostel?: number | null;
  total_rooms_in_hostel?: number | null;
  selected_departments?: string[] | null;
  selected_facilities?: string[] | null;
  selected_levels?: string[] | null;
  departments?: string[] | null;
  facilities?: string[] | null;
  board?: string | null;
  medium?: string | string[] | null;
  accreditation?: string | null;
  year_established?: number | null;
  affiliation_status?: string | null;
  affiliation_number?: string | null;
  recognition_number?: string | null;
  contact_person?: string | null;
  district?: string | null;
  classrooms?: number | null;
  campus_area?: number | null;
  staff_count?: number | null;
  trustee_designation?: string | null;
  trustee_email?: string | null;
  trustee_count?: number | null;
  trust_registration?: string | null;
  other_trustees?: string | null;
  comments?: string | null;
  image?: string[] | { url: string }[] | null;
  updated_at?: string;
  boarding_gender?: string | null;
  designation?: string | null;
  admission_link?: string | null;
  brochure?: string | null;
  educational_year?: Array<{
    educational_year: string;
    total_capacity: number;
    vacant: number;
    occupied?: number;
  }> | null;
  /** From GET /jbf/institution-approvals/{id}/. Max 2 votes: super_admin + management or management + management. */
  vote_info?: InstitutionVoteInfo | null;
};

const institutionDetailInFlight = new Map<
  string,
  Promise<InstitutionDetailData>
>();

/** Same 5 steps as Institutions.tsx + Review & Action */
const steps = [
  ...INSTITUTION_ONBOARDING_STEPS.map(s => s.title),
  "Review Application",
];

type FacilityOption = {
  id: string;
  name: string;
};

type LocationOption = {
  id: string;
  name: string;
};

export default function InstitutionDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = usePrefixedNavigate();
  const [institution, setInstitution] = useState<InstitutionDetailData | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [editForm, setEditForm] = useState<Partial<InstitutionDetailData>>({});
  const [actionReason, setActionReason] = useState("");
  /** Institution IDs this management user has approved this session (so we hide Approve button until API sends current_user_approved) */
  const [approvedByMeInSession, setApprovedByMeInSession] = useState<
    Set<string>
  >(() => new Set());
  const [facilityOptions, setFacilityOptions] = useState<FacilityOption[]>([]);
  const [loadingFacilities, setLoadingFacilities] = useState(false);
  const [designationOptions, setDesignationOptions] = useState<
    DesignationMaster[]
  >([]);
  const [loadingDesignations, setLoadingDesignations] = useState(false);
  const [mainStateOption, setMainStateOption] = useState<LocationOption | null>(
    null
  );
  const [mainDistrictOption, _setMainDistrictOption] =
    useState<LocationOption | null>(null);
  const [mainCityOption, _setMainCityOption] = useState<LocationOption | null>(
    null
  );
  const [stateOption, setStateOption] = useState<LocationOption | null>(null);
  const [districtOption, _setDistrictOption] = useState<LocationOption | null>(
    null
  );
  const [cityOption, _setCityOption] = useState<LocationOption | null>(null);
  const previousIdRef = useRef<string | undefined>(undefined);

  // Media Lightbox State
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<{
    url: string;
    type: "image" | "video";
  } | null>(null);

  const isMediaVideo = (url: string) => {
    if (!url) {
      return false;
    }
    const videoExtensions = [".mp4", ".webm", ".ogg", ".mov", ".m4v"];
    const lowercaseUrl = url.toLowerCase();
    return (
      videoExtensions.some(ext => lowercaseUrl.endsWith(ext)) ||
      lowercaseUrl.includes("video")
    );
  };

  const handleMediaClick = (url: string) => {
    setSelectedMedia({
      url,
      type: isMediaVideo(url) ? "video" : "image",
    });
    setLightboxOpen(true);
  };

  const voteInfo = institution?.vote_info;
  const superAdminApproved = voteInfo?.super_admin?.approved === true;
  const totalVotes = voteInfo?.total_votes ?? 0;
  const MAX_VOTES = 3;

  // Current user role: from Auth context or localStorage userInfo (for approval UI: super_admin vs management)
  const { user: authUser } = useAuth();
  const userInfo = getUserInfo();
  const jbfRole = authUser?.jbf_role ?? userInfo?.jbf_role ?? null;
  const isSuperAdmin = jbfRole === "super_admin";
  const isManagement = jbfRole === "management";
  /** Current management user has already approved → show only Reject. Use API current_user_approved when present; else use session track after this user clicks Approve so Approve is not shown a second time. */
  const currentUserApproved = voteInfo?.current_user_approved;
  const managementAlreadyApproved =
    isManagement &&
    (currentUserApproved === true ||
      (id !== null && approvedByMeInSession.has(String(id))));
  const managementApprovedCount = voteInfo?.management?.approved_count ?? 0;

  const fetchInstitution = useCallback(async () => {
    if (!id) {
      return;
    }
    try {
      let request = institutionDetailInFlight.get(id);
      if (!request) {
        request = institutionsAPI
          .get(id)
          .then((res: unknown) => {
            const raw = res as Record<string, unknown>;
            const data = (
              raw?.data !== null && typeof raw.data === "object"
                ? raw.data
                : raw
            ) as InstitutionDetailData & {
              institution_images?: unknown;
              images?: unknown;
            };
            const validStr = (v: unknown): v is string =>
              typeof v === "string" && v.trim().length > 0;
            let educational_year: InstitutionDetailData["educational_year"] =
              data.educational_year;
            if (typeof educational_year === "string") {
              try {
                const parsed = JSON.parse(educational_year);
                educational_year = Array.isArray(parsed) ? parsed : null;
              } catch {
                educational_year = null;
              }
            }
            if (!Array.isArray(educational_year)) {
              educational_year = null;
            }
            return {
              ...data,
              image:
                data.image ?? data.institution_images ?? data.images ?? null,
              selected_levels: (data.selected_levels ?? []).filter(
                validStr
              ) as string[],
              selected_departments: (
                data.selected_departments ??
                (data as any).departments ??
                []
              ).filter(validStr) as string[],
              selected_facilities: (
                data.selected_facilities ??
                (data as any).facilities ??
                []
              ).filter(validStr) as string[],
              educational_year,
              vote_info: (raw?.vote_info ?? (raw as any)?.data?.vote_info) as
                | InstitutionVoteInfo
                | undefined,
            } as InstitutionDetailData;
          })
          .finally(() => {
            institutionDetailInFlight.delete(id);
          });
        institutionDetailInFlight.set(id, request);
      }

      const data = await request;
      setInstitution(data);
      setEditForm(data);
    } catch (error: unknown) {
      console.error("Error fetching institution:", error);
      toast.error("Failed to fetch institution details");
      navigate("/super-admin/institutions");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (id) {
      previousIdRef.current = id;
      fetchInstitution();
    }
  }, [id, fetchInstitution]);

  useEffect(() => {
    let mounted = true;
    const loadFacilities = async () => {
      setLoadingFacilities(true);
      try {
        const list = await facilitiesMasterAPI.list();
        if (!mounted) {
          return;
        }
        setFacilityOptions(
          list
            .filter(
              item => item.is_active !== false && String(item.name ?? "").trim()
            )
            .sort((a, b) => {
              const orderA = a.order_by ?? Number.MAX_SAFE_INTEGER;
              const orderB = b.order_by ?? Number.MAX_SAFE_INTEGER;
              if (orderA !== orderB) {
                return orderA - orderB;
              }
              return String(a.name).localeCompare(String(b.name));
            })
            .map(item => ({
              id: String(item.id),
              name: String(item.name).trim(),
            }))
        );
      } catch {
        if (!mounted) {
          return;
        }
        setFacilityOptions([]);
      } finally {
        if (mounted) {
          setLoadingFacilities(false);
        }
      }
    };

    loadFacilities();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadDesignations = async () => {
      setLoadingDesignations(true);
      try {
        const list = await designationMasterAPI.list();
        if (!mounted) {
          return;
        }
        setDesignationOptions(list.filter(d => d.is_active !== false));
      } catch {
        if (!mounted) {
          return;
        }
        setDesignationOptions([]);
      } finally {
        if (mounted) {
          setLoadingDesignations(false);
        }
      }
    };
    loadDesignations();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const resolveLocationLabels = async () => {
      const stateMainId = String(institution?.state_main ?? "").trim();
      const districtMainId = String(institution?.district_main ?? "").trim();
      const cityMainId = String(institution?.city_main ?? "").trim();

      const stateId = String(institution?.state ?? "").trim();
      const districtId = String(institution?.district ?? "").trim();
      const cityId = String(institution?.city ?? "").trim();

      if (
        !stateMainId &&
        !districtMainId &&
        !cityMainId &&
        !stateId &&
        !districtId &&
        !cityId
      ) {
        return;
      }

      try {
        // Resolve Step 1
        if (stateMainId) {
          const states = await statesAPI.list();
          if (mounted) {
            const match = states.find(s => String(s.id) === stateMainId);
            setMainStateOption(
              match
                ? { id: String(match.id), name: match.name }
                : { id: stateMainId, name: stateMainId }
            );

            // if (districtMainId) {
            //   const districts =
            //     await statesAPI.getDistrictsByStateId(stateMainId);
            //   if (mounted) {
            //     const dMatch = districts.find(
            //       d => String(d.id) === districtMainId
            //     );
            //     setMainDistrictOption(
            //       dMatch
            //         ? { id: String(dMatch.id), name: dMatch.name }
            //         : { id: districtMainId, name: districtMainId }
            //     );

            //     if (cityMainId) {
            //       const cities =
            //         await districtsAPI.getCitiesByDistrictId(districtMainId);
            //       if (mounted) {
            //         const cMatch = cities.find(
            //           c => String(c.id) === cityMainId
            //         );
            //         setMainCityOption(
            //           cMatch
            //             ? { id: String(cMatch.id), name: cMatch.name }
            //             : { id: cityMainId, name: cityMainId }
            //         );
            //       }
            //     }
            //   }
            // }
          }
        }

        // Resolve Step 2
        if (stateId) {
          if (mounted) {
            const states = await statesAPI.list();
            const match = states.find(s => String(s.id) === stateId);
            setStateOption(
              match
                ? { id: String(match.id), name: match.name }
                : { id: stateId, name: stateId }
            );

            // if (districtId) {
            //   const districts = await statesAPI.getDistrictsByStateId(stateId);
            //   if (mounted) {
            //     const dMatch = districts.find(d => String(d.id) === districtId);
            //     setDistrictOption(
            //       dMatch
            //         ? { id: String(dMatch.id), name: dMatch.name }
            //         : { id: districtId, name: districtId }
            //     );

            //     if (cityId) {
            //       const cities =
            //         await districtsAPI.getCitiesByDistrictId(districtId);
            //       if (mounted) {
            //         const cMatch = cities.find(c => String(c.id) === cityId);
            //         setCityOption(
            //           cMatch
            //             ? { id: String(cMatch.id), name: cMatch.name }
            //             : { id: cityId, name: cityId }
            //         );
            //       }
            //     }
            //   }
            // }
          }
        }
      } catch (error) {
        console.error("Error resolving location labels:", error);
      }
    };

    resolveLocationLabels();
    return () => {
      mounted = false;
    };
  }, [institution]);

  const handleNext = () => {
    if (currentStep < 6) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // const handleSave = async () => {
  //   if (!institution) {
  //     return;
  //   }

  //   setSaving(true);
  //   try {
  //     const validStr = (v: unknown): v is string =>
  //       typeof v === "string" && v.trim().length > 0;
  //     const payload = {
  //       name: editForm.name ?? null,
  //       type: editForm.type ?? null,
  //       email: editForm.email ?? null,
  //       phone: editForm.phone ?? null,
  //       website: editForm.website ?? null,
  //       contact_person: editForm.contact_person ?? null,
  //       affiliation_status: editForm.affiliation_status ?? null,
  //       year_established: editForm.year_established ?? null,
  //       address_line1: editForm.address_line1 ?? null,
  //       address_line2: editForm.address_line2 ?? null,
  //       city: editForm.city ?? null,
  //       district: editForm.district ?? null,
  //       state: editForm.state ?? null,
  //       pincode: editForm.pincode ?? null,
  //       board: editForm.board ?? null,
  //       medium: editForm.medium ?? null,
  //       accreditation: editForm.accreditation ?? null,
  //       affiliation_number: editForm.affiliation_number ?? null,
  //       recognition_number: editForm.recognition_number ?? null,
  //       selected_levels: (editForm.selected_levels ?? []).filter(validStr)
  //         .length
  //         ? (editForm.selected_levels ?? []).filter(validStr)
  //         : null,
  //       total_students: editForm.total_students ?? null,
  //       staff_count: editForm.staff_count ?? null,
  //       teaching_staff: editForm.teaching_staff ?? null,
  //       non_teaching_staff: editForm.non_teaching_staff ?? null,
  //       classrooms: editForm.classrooms ?? null,
  //       campus_area: editForm.campus_area ?? null,
  //       selected_departments: (
  //         editForm.selected_departments ??
  //         editForm.departments ??
  //         []
  //       ).filter(validStr).length
  //         ? (
  //           editForm.selected_departments ??
  //           editForm.departments ??
  //           []
  //         ).filter(validStr)
  //         : null,
  //       selected_facilities: (
  //         editForm.selected_facilities ??
  //         editForm.facilities ??
  //         []
  //       ).filter(validStr).length
  //         ? (editForm.selected_facilities ?? editForm.facilities ?? []).filter(
  //           validStr
  //         )
  //         : null,
  //       trustee_name: editForm.trustee_name ?? null,
  //       trustee_contact: editForm.trustee_contact ?? null,
  //       trustee_email: editForm.trustee_email ?? null,
  //       trustee_designation: editForm.trustee_designation ?? null,
  //       trustee_count: editForm.trustee_count ?? null,
  //       trust_registration: editForm.trust_registration ?? null,
  //       other_trustees: editForm.other_trustees ?? null,
  //       comments: editForm.comments ?? null,
  //     };
  //     await institutionsAPI.update(
  //       String(institution.id),
  //       payload as Partial<Institution>
  //     );

  //     toast.success("Institution updated successfully");
  //     setIsEditing(false);
  //     institutionDetailCache.delete(String(institution.id));
  //     fetchInstitution();
  //   } catch (error: any) {
  //     console.error("Error updating institution:", error);
  //     toast.error("Failed to update institution");
  //   } finally {
  //     setSaving(false);
  //   }
  // };

  const handleStatusChange = async (newStatus: "approved" | "rejected") => {
    if (!institution) {
      return;
    }

    if (newStatus === "rejected" && !actionReason.trim()) {
      toast.error(
        "Please provide a Notes / Reason to reject this institution."
      );
      return;
    }

    setSaving(true);
    try {
      if (newStatus === "approved") {
        const res = (await institutionsAPI.approve(
          String(institution.id)
        )) as InstitutionApproveResponse;

        if (!res) {
          toast.error("Failed to approve institution. Please try again.");
          return;
        }

        const raw = res ?? (res as any)?.data;
        const message = raw?.message ?? "Institution approved successfully";
        toast.success(message);
        const jbfRole = authUser?.jbf_role ?? getUserInfo()?.jbf_role;
        if (jbfRole === "management") {
          setApprovedByMeInSession(prev =>
            new Set(prev).add(String(institution.id))
          );
        }
      } else {
        const res = await institutionsAPI.reject(
          String(institution.id),
          actionReason
        );

        if (!res) {
          toast.error("Failed to reject institution. Please try again.");
          return;
        }

        toast.success("Institution rejected successfully");
        setApprovedByMeInSession(prev => {
          const next = new Set(prev);
          next.delete(String(institution.id));
          return next;
        });
      }

      setActionReason("");
      await fetchInstitution();
    } catch (error: any) {
      console.error("Error updating status:", error);
      toast.error("Failed to update institution status");
    } finally {
      setSaving(false);
    }
  };

  const getStatusConfig = (status: string | null) => {
    switch (status) {
      case "approved":
        return {
          icon: CheckCircle,
          label: "Approved",
          className: "bg-green-100 text-green-800 border-green-300",
          iconClass: "text-green-600",
        };
      case "rejected":
        return {
          icon: XCircle,
          label: "Rejected",
          className: "bg-red-100 text-red-800 border-red-300",
          iconClass: "text-red-600",
        };
      default:
        return {
          icon: Clock,
          label: "Pending Review",
          className: "bg-yellow-100 text-yellow-800 border-yellow-300",
          iconClass: "text-yellow-600",
        };
    }
  };

  const updateFormData = (field: keyof InstitutionDetailData, value: any) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const toggleLevel = (levelValue: string) => {
    const raw = editForm.selected_levels ?? institution?.selected_levels ?? [];
    const current = raw.filter(
      (l: string) => typeof l === "string" && l.trim().length > 0
    );
    const next = current.includes(levelValue)
      ? current.filter((l: string) => l !== levelValue)
      : [...current, levelValue];
    updateFormData("selected_levels", next);
  };

  const normalizeFacilityIds = (raw: unknown[]): string[] => {
    return Array.from(
      new Set(
        raw
          .filter((value): value is string => typeof value === "string")
          .map(value => value.trim())
          .filter(Boolean)
          .map(value => {
            const matchedById = facilityOptions.find(item => item.id === value);
            if (matchedById) {
              return matchedById.id;
            }
            const matchedByName = facilityOptions.find(
              item => item.name === value
            );
            return matchedByName?.id ?? value;
          })
      )
    );
  };

  const getFacilityLabel = (value: string) => {
    return (
      facilityOptions.find(item => item.id === value || item.name === value)
        ?.name ?? value
    );
  };

  const getDesignationLabel = (value: string | undefined | null) => {
    if (!value) {
      return "Not provided";
    }
    return (
      designationOptions.find(
        d =>
          String(d.id) === String(value) ||
          d.name.toLowerCase() === value.toLowerCase()
      )?.name ?? value
    );
  };

  const getMediumLabels = (value: InstitutionDetailData["medium"]) => {
    const values = Array.isArray(value)
      ? value
      : typeof value === "string" && value
        ? value.split("/").filter(Boolean)
        : [];

    return values.map(
      item =>
        MEDIUM_OF_INSTRUCTION.find(option => option.value === item)?.label ??
        item
    );
  };

  const toggleFacility = (facilityId: string) => {
    const raw =
      editForm.selected_facilities ??
      institution?.selected_facilities ??
      institution?.facilities ??
      [];
    const current = normalizeFacilityIds(raw);
    const next = current.includes(facilityId)
      ? current.filter((f: string) => f !== facilityId)
      : [...current, facilityId];
    updateFormData("selected_facilities", next);
  };

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center p-6">
        <div className="space-y-3 text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
          <p className="text-muted-foreground">
            Loading institution details...
          </p>
        </div>
      </div>
    );
  }

  if (!institution) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Institution not found</p>
      </div>
    );
  }

  const statusConfig = getStatusConfig(institution.approval_status ?? null);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="icon"
          onClick={() => navigate("/super-admin/institutions")}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-foreground">
            Review Institution
          </h1>
          <p className="text-muted-foreground">
            Step {currentStep} of 6: {steps[currentStep - 1]}
          </p>
        </div>
        <Badge
          className={`${statusConfig.className} px-3 py-1.5 text-sm font-medium`}
        >
          <StatusIcon className={`mr-1.5 h-4 w-4 ${statusConfig.iconClass}`} />
          {statusConfig.label}
        </Badge>
        {/* {!isEditing ? (
          <Button variant="outline" onClick={() => setIsEditing(true)}>
            <Edit className="mr-2 h-4 w-4" />
            Edit Mode
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsEditing(false);
                setEditForm(institution);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              <Save className="mr-2 h-4 w-4" />
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        )} */}
      </div>

      {/* Progress Stepper */}
      <div className="flex items-center justify-between">
        {steps.map((step, index) => (
          <div key={step} className="flex flex-1 items-center">
            <div className="flex flex-col items-center">
              <button
                onClick={() => setCurrentStep(index + 1)}
                className={`flex h-10 w-10 items-center justify-center rounded-full border-2 transition-colors ${
                  index + 1 <= currentStep
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-muted bg-background hover:border-primary/50"
                }`}
              >
                {index + 1 < currentStep ? (
                  <Check className="h-5 w-5" />
                ) : (
                  index + 1
                )}
              </button>
              <span className="mt-2 max-w-[80px] text-center text-xs">
                {step}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`mx-2 h-0.5 flex-1 ${index + 1 < currentStep ? "bg-primary" : "bg-muted"}`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Form Content */}
      <Card>
        <CardHeader>
          <CardTitle>{steps[currentStep - 1]}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Step 1: Basic Information (same as Institutions.tsx) */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-sm font-medium">
                    Institution Name
                  </Label>
                  {isEditing ? (
                    <Input
                      id="name"
                      value={editForm.name ?? ""}
                      onChange={e => updateFormData("name", e.target.value)}
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {toInstitutionTitleCase(
                          String(institution.name ?? "-")
                        )}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type" className="text-sm font-medium">
                    Institution Type
                  </Label>
                  {isEditing ? (
                    <Select
                      value={editForm.type ?? ""}
                      onValueChange={v => updateFormData("type", v)}
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {INSTITUTION_TYPES.map(t => (
                          <SelectItem key={t.value} value={t.value}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium capitalize">
                        {institution.type ?? "Not specified"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    Year Established
                  </Label>
                  {isEditing ? (
                    <Input
                      type="number"
                      min={0}
                      value={editForm.year_established ?? ""}
                      onWheel={e => e.currentTarget.blur()}
                      onKeyDown={e => {
                        if (["e", "E", "-", "+"].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onChange={e => {
                        const val = parseInt(e.target.value);
                        updateFormData(
                          "year_established",
                          isNaN(val) ? null : Math.max(0, val)
                        );
                      }}
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {institution.year_established ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4 md:col-span-2 lg:col-span-3">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Contact Person Name
                    </Label>
                    {isEditing ? (
                      <Input
                        value={editForm.contact_person ?? ""}
                        onChange={e =>
                          updateFormData("contact_person", e.target.value)
                        }
                        placeholder="Enter contact person name"
                        className="h-11"
                      />
                    ) : (
                      <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                        <span className="font-medium">
                          {institution.contact_person ?? "Not provided"}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Designation</Label>
                    {isEditing ? (
                      <Select
                        value={editForm.designation ?? ""}
                        onValueChange={v => updateFormData("designation", v)}
                      >
                        <SelectTrigger className="h-11">
                          <SelectValue
                            placeholder={
                              loadingDesignations
                                ? "Loading..."
                                : "Select designation"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {designationOptions.map(d => (
                            <SelectItem key={d.id} value={String(d.id)}>
                              {d.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                        <span className="font-medium">
                          {getDesignationLabel(institution.designation)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium">
                    Email Address
                  </Label>
                  {isEditing ? (
                    <Input
                      id="email"
                      type="email"
                      value={editForm.email ?? ""}
                      onChange={e => updateFormData("email", e.target.value)}
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center gap-3 rounded-lg border bg-muted/50 px-4">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">
                        {institution.email ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm font-medium">
                    Phone Number
                  </Label>
                  {isEditing ? (
                    <Input
                      id="phone"
                      value={editForm.phone ?? ""}
                      maxLength={10}
                      onChange={e =>
                        updateFormData(
                          "phone",
                          e.target.value.replace(/\D/g, "")
                        )
                      }
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center gap-3 rounded-lg border bg-muted/50 px-4">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">
                        {institution.phone ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2 md:col-span-2 lg:col-span-3">
                  <Label htmlFor="website" className="text-sm font-medium">
                    Website
                  </Label>
                  {isEditing ? (
                    <Input
                      id="website"
                      value={editForm.website ?? ""}
                      onChange={e => updateFormData("website", e.target.value)}
                      placeholder="https://example.com"
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center gap-3 rounded-lg border bg-muted/50 px-4">
                      <Globe className="h-4 w-4 text-muted-foreground" />
                      {institution.website ? (
                        <a
                          href={institution.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="truncate font-medium text-primary hover:underline"
                        >
                          {institution.website}
                        </a>
                      ) : (
                        <span className="font-medium text-muted-foreground">
                          Not provided
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Main State</Label>
                  <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                    <span className="font-medium">
                      {institution.state_main_name ??
                        mainStateOption?.name ??
                        institution.state_main ??
                        institution.state_name ??
                        institution.state ??
                        "Not provided"}
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Main District</Label>
                  <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                    <span className="font-medium">
                      {institution.district_main_name ??
                        mainDistrictOption?.name ??
                        institution.district_main ??
                        institution.district_name ??
                        institution.district ??
                        "Not provided"}
                    </span>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Main City</Label>
                  <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                    <span className="font-medium">
                      {institution.city_main_name ??
                        mainCityOption?.name ??
                        institution.city_main ??
                        institution.city_name ??
                        institution.city ??
                        "Not provided"}
                    </span>
                  </div>
                </div>
                {/* <div className="space-y-2">
                  <Label className="text-sm font-medium">Main Area</Label>
                  <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                    <span className="font-medium">
                      {mainAreaOption?.name ??
                        institution.area_main ??
                        "Not provided"}
                    </span>
                  </div>
                </div> */}
                <div className="space-y-2 md:col-span-2 lg:col-span-3">
                  <Label className="text-sm font-medium">
                    Institution Media (Images & Videos)
                  </Label>
                  <div className="grid grid-cols-3 gap-3 rounded-lg border bg-muted/20 p-4 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8">
                    {((institution.image ?? []) as (string | { url: string })[])
                      .length > 0 ? (
                      (
                        (institution.image ?? []) as (
                          | string
                          | { url: string }
                        )[]
                      ).map((img: string | { url: string }, index) => {
                        const url = typeof img === "string" ? img : img.url;
                        const isVideo = isMediaVideo(url);
                        return (
                          <div
                            key={url}
                            className="group relative aspect-square cursor-pointer overflow-hidden rounded-lg border border-background bg-muted shadow-sm transition-all hover:z-10 hover:scale-[1.05] hover:shadow-md"
                            onClick={() => handleMediaClick(url)}
                          >
                            {isVideo ? (
                              <div className="h-full w-full">
                                <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/10 transition-colors group-hover:bg-black/30">
                                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white shadow-md transition-transform group-hover:scale-110">
                                    <Play className="ml-0.5 h-4 w-4 fill-current" />
                                  </div>
                                </div>
                                <video
                                  src={url}
                                  className="h-full w-full object-cover"
                                />
                              </div>
                            ) : (
                              <div className="relative h-full w-full">
                                <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-300 group-hover:bg-black/20 group-hover:opacity-100">
                                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-primary shadow-md">
                                    <Eye className="h-4 w-4" />
                                  </div>
                                </div>
                                <img
                                  src={url}
                                  alt={`Institution Media ${index + 1}`}
                                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                                />
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="col-span-full flex flex-col items-center justify-center gap-3 py-12 text-muted-foreground">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                          <Upload className="h-8 w-8 opacity-50" />
                        </div>
                        <div className="text-center">
                          <p className="font-medium">No media uploaded</p>
                          <p className="text-sm">
                            Institution hasn&apos;t provided any images or
                            videos yet.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Address (same as Institutions.tsx) */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2 md:col-span-2">
                  <Label
                    htmlFor="address_line1"
                    className="text-sm font-medium"
                  >
                    Address Line 1
                  </Label>
                  {isEditing ? (
                    <Input
                      id="address_line1"
                      value={editForm.address_line1 ?? ""}
                      onChange={e =>
                        updateFormData("address_line1", e.target.value)
                      }
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {institution.address_line1 ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label
                    htmlFor="address_line2"
                    className="text-sm font-medium"
                  >
                    Address Line 2
                  </Label>
                  {isEditing ? (
                    <Input
                      id="address_line2"
                      value={editForm.address_line2 ?? ""}
                      onChange={e =>
                        updateFormData("address_line2", e.target.value)
                      }
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {institution.address_line2 ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="district" className="text-sm font-medium">
                    District
                  </Label>
                  {isEditing ? (
                    <Input
                      id="district"
                      value={editForm.district ?? ""}
                      onChange={e => updateFormData("district", e.target.value)}
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {institution.district_name ??
                          districtOption?.name ??
                          institution.district ??
                          "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city" className="text-sm font-medium">
                    City
                  </Label>
                  {isEditing ? (
                    <Input
                      id="city"
                      value={editForm.city ?? ""}
                      onChange={e => updateFormData("city", e.target.value)}
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {institution.city_name ??
                          cityOption?.name ??
                          institution.city ??
                          "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state" className="text-sm font-medium">
                    State
                  </Label>
                  {isEditing ? (
                    <Input
                      id="state"
                      value={editForm.state ?? ""}
                      onChange={e => updateFormData("state", e.target.value)}
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {institution.state_name ??
                          stateOption?.name ??
                          institution.state ??
                          "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pincode" className="text-sm font-medium">
                    Pincode
                  </Label>
                  {isEditing ? (
                    <Input
                      id="pincode"
                      value={editForm.pincode ?? ""}
                      maxLength={6}
                      onChange={e =>
                        updateFormData(
                          "pincode",
                          e.target.value.replace(/\D/g, "")
                        )
                      }
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {institution.pincode ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: School Details (Only if its applicable) (same as Institutions.tsx) */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    Board / University
                  </Label>
                  {isEditing ? (
                    <Select
                      value={editForm.board ?? ""}
                      onValueChange={v => updateFormData("board", v)}
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Select board" />
                      </SelectTrigger>
                      <SelectContent>
                        {BOARDS.map(b => (
                          <SelectItem key={b.value} value={b.value}>
                            {b.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium capitalize">
                        {institution.board ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    Medium of Instruction
                  </Label>
                  <div className="flex min-h-[60px] flex-wrap gap-2 rounded-lg border bg-muted/50 p-4">
                    {(() => {
                      const mediumLabels = getMediumLabels(
                        editForm.medium ?? institution.medium ?? null
                      );
                      return mediumLabels.length > 0 ? (
                        mediumLabels.map(label => (
                          <Badge
                            key={label}
                            variant="secondary"
                            className="border-0 bg-primary/10 px-3 py-1.5 text-primary"
                          >
                            {label}
                          </Badge>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          No medium selected
                        </p>
                      );
                    })()}
                  </div>
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label className="text-sm font-medium">
                    Education Levels Offered
                  </Label>
                  {isEditing ? (
                    <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-4">
                      {INSTITUTION_LEVELS.map(level => {
                        const raw =
                          editForm.selected_levels ??
                          institution?.selected_levels ??
                          [];
                        const valid = raw.filter(
                          (l: string) =>
                            typeof l === "string" && l.trim().length > 0
                        );
                        const selected = valid.includes(level.value);
                        return (
                          <div
                            key={level.value}
                            className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 transition-colors ${selected ? "border-primary bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                            onClick={() => toggleLevel(level.value)}
                          >
                            <CheckCircle
                              className={`h-4 w-4 ${selected ? "opacity-100" : "opacity-0"}`}
                            />
                            <span className="text-sm">{level.label}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex min-h-[60px] flex-wrap gap-2 rounded-lg border bg-muted/50 p-4">
                      {(() => {
                        const levels = (
                          institution.selected_levels ?? []
                        ).filter(
                          (l: string) =>
                            typeof l === "string" && l.trim().length > 0
                        );
                        return levels.length > 0 ? (
                          levels.map((level: string) => (
                            <Badge
                              key={level}
                              variant="secondary"
                              className="border-0 bg-primary/10 px-3 py-1.5 text-primary"
                            >
                              {INSTITUTION_LEVELS.find(l => l.value === level)
                                ?.label ?? level.replace(/_/g, " ")}
                            </Badge>
                          ))
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            No levels added
                          </p>
                        );
                      })()}
                    </div>
                  )}
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label className="text-sm font-medium">Hostel Gender</Label>
                  <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                    <span className="font-medium capitalize">
                      {(() => {
                        const raw = institution.boarding_gender ?? "";
                        if (!raw) {
                          return "Not provided";
                        }
                        const parts =
                          typeof raw === "string"
                            ? raw.split("/").filter(Boolean)
                            : [];
                        return parts.length > 0
                          ? parts
                              .map(
                                p =>
                                  BOARDING_GENDER.find(
                                    g => g.value === p.trim()
                                  )?.label ?? p
                              )
                              .join(", ")
                          : "Not provided";
                      })()}
                    </span>
                  </div>
                </div>
                {/* <div className="space-y-2 md:col-span-2">
                  <Label className="text-sm font-medium">
                    Educational Year & Capacity
                  </Label>
                  <div className="overflow-hidden rounded-lg border bg-muted/30">
                    {(institution.educational_year ?? []).length > 0 ? (
                      <>
                        <div className="grid grid-cols-[1fr_1fr_1fr_1fr] gap-3 border-b bg-muted/50 px-3 py-2 text-xs font-medium text-muted-foreground">
                          <div>Educational Year</div>
                          <div>Total Capacity</div>
                          <div>Occupied</div>
                          <div>Vacant</div>
                        </div>
                        {(institution.educational_year ?? []).map(
                          (row: {
                            educational_year: string;
                            total_capacity: number;
                            vacant: number;
                            occupied?: number;
                          }) => (
                            <div
                              key={`${row.educational_year}`}
                              className="grid grid-cols-[1fr_1fr_1fr_1fr] items-center gap-3 border-b px-3 py-2 text-sm last:border-b-0"
                            >
                              <div className="font-medium">
                                {row.educational_year}
                              </div>
                              <div>{row.total_capacity}</div>
                              <div>{row.occupied ?? 0}</div>
                              <div>{row.vacant}</div>
                            </div>
                          )
                        )}
                      </>
                    ) : (
                      <div className="px-4 py-3 text-sm text-muted-foreground">
                        No entries added
                      </div>
                    )}
                  </div>
                </div> */}
                <div className="space-y-2 md:col-span-2 lg:col-span-2">
                  <Label className="text-sm font-medium">
                    Registration Number
                  </Label>
                  {isEditing ? (
                    <Input
                      value={editForm.recognition_number ?? ""}
                      onChange={e =>
                        updateFormData("recognition_number", e.target.value)
                      }
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {institution.recognition_number ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4 md:col-span-2 lg:col-span-3">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Admission Link
                    </Label>
                    <div className="flex h-11 items-center gap-3 rounded-lg border bg-muted/50 px-4">
                      <Globe className="h-4 w-4 text-muted-foreground" />
                      {institution.admission_link ? (
                        <a
                          href={
                            institution.admission_link.startsWith("http")
                              ? institution.admission_link
                              : `https://${institution.admission_link}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="truncate font-medium text-primary hover:underline"
                        >
                          {institution.admission_link}
                        </a>
                      ) : (
                        <span className="font-medium text-muted-foreground">
                          Not provided
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Brochure</Label>
                    <div className="flex h-11 items-center gap-3 rounded-lg border bg-muted/50 px-4">
                      <Upload className="h-4 w-4 text-muted-foreground" />
                      {institution.brochure ? (
                        <a
                          href={institution.brochure}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="truncate font-medium text-primary hover:underline"
                        >
                          View Brochure
                        </a>
                      ) : (
                        <span className="font-medium text-muted-foreground">
                          Not uploaded
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Capacity & Facilities */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-3 rounded-lg border bg-muted/20 p-4">
                  <h4 className="text-sm font-semibold">School Capacity</h4>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Total Students (In School)
                    </Label>
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {(
                          institution.total_students_in_school ??
                          institution.total_students
                        )?.toLocaleString() ?? "—"}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Total Classroom (In School)
                    </Label>
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {(
                          institution.total_classroom_in_school ??
                          institution.classrooms
                        )?.toLocaleString() ?? "—"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 rounded-lg border bg-muted/20 p-4">
                  <h4 className="text-sm font-semibold">Hostel Capacity</h4>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Total Students (In Hostel)
                    </Label>
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {institution.total_students_in_hostel?.toLocaleString() ??
                          "—"}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">
                      Total Rooms (In Hostel)
                    </Label>
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {institution.total_rooms_in_hostel?.toLocaleString() ??
                          "—"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-sm font-medium">Campus Area</Label>
                <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                  <span className="font-medium">
                    {institution.campus_area
                      ? String(institution.campus_area)
                      : "Not provided"}
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Facilities Available
                </Label>
                {isEditing ? (
                  <div className="mt-2 grid grid-cols-2 gap-3 md:grid-cols-4">
                    {loadingFacilities ? (
                      <div className="col-span-full flex items-center gap-2 rounded-lg border px-3 py-4 text-sm text-muted-foreground">
                        <Upload className="h-4 w-4" />
                        Loading facilities...
                      </div>
                    ) : facilityOptions.length === 0 ? (
                      <div className="col-span-full rounded-lg border px-3 py-4 text-sm text-muted-foreground">
                        No facilities available in Facilities Master.
                      </div>
                    ) : (
                      facilityOptions.map(facility => {
                        const raw =
                          editForm.selected_facilities ??
                          institution?.selected_facilities ??
                          institution?.facilities ??
                          [];
                        const valid = normalizeFacilityIds(raw);
                        const selected = valid.includes(facility.id);
                        return (
                          <div
                            key={facility.id}
                            className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 transition-colors ${selected ? "border-primary bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                            onClick={() => toggleFacility(facility.id)}
                          >
                            <CheckCircle
                              className={`h-4 w-4 ${selected ? "opacity-100" : "opacity-0"}`}
                            />
                            <span className="text-sm">{facility.name}</span>
                          </div>
                        );
                      })
                    )}
                  </div>
                ) : (
                  <div className="flex min-h-[60px] flex-wrap gap-2 rounded-lg border bg-muted/50 p-4">
                    {(() => {
                      const facs = normalizeFacilityIds(
                        institution.selected_facilities ??
                          institution.facilities ??
                          []
                      );
                      return facs.length > 0 ? (
                        facs.map((fac: string) => (
                          <Badge
                            key={fac}
                            variant="secondary"
                            className="border-0 bg-primary/10 px-3 py-1.5 text-primary"
                          >
                            {getFacilityLabel(fac)}
                          </Badge>
                        ))
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          No facilities added
                        </p>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 5: Trustee / Management Information */}
          {currentStep === 5 && (
            <div className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    Trustee / Principal Name
                  </Label>
                  {isEditing ? (
                    <Input
                      value={editForm.trustee_name ?? ""}
                      onChange={e =>
                        updateFormData("trustee_name", e.target.value)
                      }
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center gap-3 rounded-lg border bg-muted/50 px-4">
                      <Shield className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">
                        {institution.trustee_name ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Designation</Label>
                  {isEditing ? (
                    <Select
                      value={editForm.trustee_designation ?? ""}
                      onValueChange={v =>
                        updateFormData("trustee_designation", v)
                      }
                    >
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Select designation" />
                      </SelectTrigger>
                      <SelectContent>
                        {designationOptions.map(d => (
                          <SelectItem key={d.id} value={String(d.id)}>
                            {d.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {getDesignationLabel(institution.trustee_designation)}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Trustee Phone</Label>
                  {isEditing ? (
                    <Input
                      value={editForm.trustee_contact ?? ""}
                      maxLength={10}
                      onChange={e =>
                        updateFormData(
                          "trustee_contact",
                          e.target.value.replace(/\D/g, "")
                        )
                      }
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center gap-3 rounded-lg border bg-muted/50 px-4">
                      <Phone className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">
                        {institution.trustee_contact ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Trustee Email</Label>
                  {isEditing ? (
                    <Input
                      type="email"
                      value={editForm.trustee_email ?? ""}
                      onChange={e =>
                        updateFormData("trustee_email", e.target.value)
                      }
                      placeholder="Enter email"
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center gap-3 rounded-lg border bg-muted/50 px-4">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">
                        {institution.trustee_email ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    Number of Trustees
                  </Label>
                  {isEditing ? (
                    <Input
                      type="number"
                      value={editForm.trustee_count ?? ""}
                      min={0}
                      onWheel={e => e.currentTarget.blur()}
                      onKeyDown={e => {
                        if (["e", "E", "-", "+"].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onChange={e => {
                        const val = parseInt(e.target.value);
                        updateFormData(
                          "trustee_count",
                          isNaN(val) ? null : Math.max(0, val)
                        );
                      }}
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {institution.trustee_count?.toLocaleString() ?? "—"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    Trust Registration Number
                  </Label>
                  {isEditing ? (
                    <Input
                      value={editForm.trust_registration ?? ""}
                      onChange={e =>
                        updateFormData("trust_registration", e.target.value)
                      }
                      placeholder="Enter registration number"
                      className="h-11"
                    />
                  ) : (
                    <div className="flex h-11 items-center rounded-lg border bg-muted/50 px-4">
                      <span className="font-medium">
                        {institution.trust_registration ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label className="text-sm font-medium">
                    Names of Other Trustees
                  </Label>
                  {isEditing ? (
                    <Textarea
                      value={editForm.other_trustees ?? ""}
                      onChange={e =>
                        updateFormData("other_trustees", e.target.value)
                      }
                      placeholder="Enter names separated by comma"
                      rows={2}
                      className="resize-none"
                    />
                  ) : (
                    <div className="min-h-[60px] rounded-lg border bg-muted/50 px-4 py-3">
                      <span className="whitespace-pre-wrap font-medium">
                        {institution.other_trustees ?? "Not provided"}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 6: Review Application */}
          {currentStep === 6 && (
            <div className="space-y-6">
              {/* Summary */}
              <div className="grid grid-cols-1 gap-4 rounded-lg bg-muted/50 p-4 md:grid-cols-2">
                <div>
                  <p className="text-sm text-muted-foreground">
                    Institution Name
                  </p>
                  <p className="font-medium">{institution.name}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Type</p>
                  <p className="font-medium capitalize">
                    {institution.type ?? "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Location</p>
                  <p className="font-medium">
                    {cityOption?.name ?? institution.city},{" "}
                    {districtOption?.name ?? institution.district},{" "}
                    {mainStateOption?.name ?? institution.state}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Contact</p>
                  <p className="font-medium">
                    {institution.email ?? institution.phone ?? "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Admission Link
                  </p>
                  <p className="truncate font-medium">
                    {institution.admission_link ?? "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Brochure</p>
                  <p className="font-medium">
                    {institution.brochure ? "Uploaded" : "No"}
                  </p>
                </div>
                {/* <div>
                  <p className="text-sm text-muted-foreground">
                    Total Students
                  </p>
                  <p className="font-medium">
                    {institution.total_students ?? 0}
                  </p>
                </div> */}
                {/* <div>
                  <p className="text-sm text-muted-foreground">Staff Count</p>
                  <p className="font-medium">
                    {(institution.teaching_staff ?? 0) +
                      (institution.non_teaching_staff ?? 0)}
                  </p>
                </div> */}
                <div>
                  <p className="text-sm text-muted-foreground">Trustee</p>
                  <p className="font-medium">
                    {institution.trustee_name ?? "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Registered On</p>
                  <p className="font-medium">
                    {formatDate(institution.created_at)}
                  </p>
                </div>
              </div>

              {/* Action Section — based on vote_info (max 3: super_admin + management + management) */}
              {institution.approval_status === "approved" && (
                <div className="rounded-lg border border-green-200 bg-green-50/50 p-4">
                  <h3 className="flex items-center gap-2 font-semibold text-green-700">
                    <CheckCircle className="h-5 w-5" />
                    All Vote approved
                  </h3>
                  <p className="mt-1 text-sm text-green-600">
                    This institution has been successfully approved by all
                    required parties.
                  </p>
                </div>
              )}

              {institution.approval_status !== "approved" &&
                institution.approval_status !== "rejected" && (
                  <div className="space-y-4 rounded-lg border border-yellow-200 bg-yellow-50/50 p-4">
                    <h3 className="flex items-center gap-2 font-semibold">
                      <Clock className="h-5 w-5 text-yellow-600" />
                      Pending Approval ({totalVotes}/{MAX_VOTES} votes)
                    </h3>

                    <div className="rounded-md bg-yellow-100/50 p-3 text-sm text-yellow-800">
                      <p className="mb-1 font-medium">Current Vote Status:</p>
                      <ul className="list-inside list-disc space-y-0.5">
                        <li>
                          Super Admin:{" "}
                          {superAdminApproved ? "Approved" : "Pending"}
                        </li>
                        <li>
                          Management: {managementApprovedCount} out of 2
                          Approved
                        </li>
                      </ul>
                    </div>

                    {isSuperAdmin && superAdminApproved && (
                      <p className="text-sm text-muted-foreground">
                        You have already approved. You can still reject if
                        needed.
                      </p>
                    )}
                    {isManagement && managementAlreadyApproved && (
                      <p className="text-sm text-muted-foreground">
                        You have already approved. You can still reject if
                        needed.
                      </p>
                    )}
                    {isManagement &&
                      !managementAlreadyApproved &&
                      managementApprovedCount >= 2 && (
                        <p className="text-sm text-muted-foreground">
                          Management has already reached the maximum of 2
                          approvals.
                        </p>
                      )}

                    {(isSuperAdmin ||
                      (isManagement &&
                        (managementApprovedCount < 2 ||
                          managementAlreadyApproved))) && (
                      <>
                        <div>
                          <Label>
                            Notes / Reason{" "}
                            {(isSuperAdmin && superAdminApproved) ||
                            (isManagement && managementAlreadyApproved)
                              ? "(for rejection)"
                              : ""}
                          </Label>
                          <Textarea
                            value={actionReason}
                            onChange={e => setActionReason(e.target.value)}
                            placeholder="Add any notes for approval or rejection reason..."
                            className="mt-1.5 bg-background"
                            rows={3}
                          />
                        </div>
                        <div className="flex gap-3">
                          {(!isSuperAdmin || !superAdminApproved) &&
                            (!isManagement || !managementAlreadyApproved) &&
                            (!isManagement || managementApprovedCount < 2) && (
                              <Button
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => handleStatusChange("approved")}
                                disabled={saving}
                              >
                                <CheckCircle className="mr-2 h-4 w-4" />
                                {saving
                                  ? "Processing..."
                                  : "Approve Institution"}
                              </Button>
                            )}
                          <Button
                            variant="destructive"
                            onClick={() => handleStatusChange("rejected")}
                            disabled={saving}
                          >
                            <XCircle className="mr-2 h-4 w-4" />
                            {saving ? "Processing..." : "Reject Institution"}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )}

              {institution.approval_status === "rejected" && (
                <div className="rounded-lg border border-red-200 bg-red-50/50 p-4">
                  <h3 className="flex items-center gap-2 font-semibold text-red-700">
                    <XCircle className="h-5 w-5" />
                    This institution has been rejected
                  </h3>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={handleBack}
          disabled={currentStep === 1}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        {currentStep < 6 && (
          <Button onClick={handleNext}>
            Next
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        )}
      </div>
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-[95vw] border-none bg-transparent p-0 shadow-none outline-none focus-visible:ring-0 sm:max-w-[90vw] md:max-w-4xl lg:max-w-5xl [&>button]:hidden">
          <div className="relative h-full w-full overflow-hidden rounded-2xl bg-zinc-950/50 backdrop-blur-md">
            {/* Custom Close Button */}
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute right-4 top-4 z-[60] flex h-10 w-10 items-center justify-center rounded-full bg-primary text-white transition-all hover:scale-110 hover:bg-red-700/80"
            >
              <X className="h-6 w-6" />
            </button>
            {selectedMedia?.type === "video" ? (
              <video
                src={selectedMedia?.url}
                className="max-h-[85vh] w-full"
                controls
                autoPlay
              />
            ) : (
              <img
                src={selectedMedia?.url}
                alt="View Media"
                className="max-h-[85vh] w-full object-contain"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
